import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom'; // Import createPortal
import { MoreVertical } from 'lucide-react';
import Button from './Button'; // Assuming Button component is available

const ActionDropdown = ({ actions }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null); // Ref for the button
  const [dropdownStyle, setDropdownStyle] = useState({}); // State for dynamic positioning

  const toggleDropdown = (e) => {
    e.stopPropagation(); // Prevent row click
    setIsOpen((prev) => !prev);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside); // Changed mousedown to click
    return () => {
      document.removeEventListener('click', handleClickOutside); // Changed mousedown to click
    };
  }, []);

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // Position the dropdown below the button, aligned to the right
      // Consider viewport boundaries to prevent overflow
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      let top = rect.bottom + window.scrollY;
      let left = rect.right + window.scrollX - 192; // 192px is w-48

      // Adjust if dropdown goes off screen to the right
      if (left + 192 > viewportWidth) {
        left = viewportWidth - 192 - 10; // 10px padding from right edge
      }
      // Adjust if dropdown goes off screen to the left (unlikely with right-alignment)
      if (left < 0) {
        left = 10; // 10px padding from left edge
      }

      // Adjust if dropdown goes off screen to the bottom
      // If not enough space below, try to open upwards
      if (top + 200 > viewportHeight + window.scrollY) {
        // 200px approx height of dropdown
        top = rect.top + window.scrollY - 200; // Position above the button
        if (top < window.scrollY) {
          // If still off screen at top, position at top of viewport
          top = window.scrollY + 10;
        }
      }

      setDropdownStyle({
        position: 'absolute',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 1000, // Higher z-index for portal
      });
    }
  }, [isOpen]);

  const dropdownContent = isOpen ? (
    <div
      className="w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
      style={dropdownStyle} // Apply dynamic style
    >
      <div className="py-1">
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={(e) => {
              action.onClick(e);
              setIsOpen(false); // Close dropdown after action
            }}
            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            role="menuitem"
          >
            {action.icon && React.createElement(action.icon, { className: 'mr-3 h-4 w-4' })}
            {action.label}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={toggleDropdown}
        className="p-2 hover:bg-gray-100 hover:border-gray-300"
        title="More actions"
        ref={buttonRef} // Attach ref to the button
      >
        <MoreVertical className="h-4 w-4 text-gray-600" />
      </Button>
      {isOpen && createPortal(dropdownContent, document.body)} {/* Render with Portal */}
    </div>
  );
};

export default ActionDropdown;
